import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { CALC_ROLES } from "@/data/content";
import { Reveal, MaskedLines, Eyebrow } from "./Reveal";
import { useLenis, scrollToId } from "@/lib/lenis";

const usd = (n) => `$${Math.round(n).toLocaleString("en-US")}`;
const SAVE_LO = 0.4;
const SAVE_HI = 0.6;

export const Calculator = () => {
  const lenis = useLenis();
  const [roleId, setRoleId] = useState(CALC_ROLES[0].id);
  const [seats, setSeats] = useState(2);
  const [usCost, setUsCost] = useState(CALC_ROLES[0].usCost);
  const role = CALC_ROLES.find((r) => r.id === roleId);

  const pickRole = (e) => {
    const r = CALC_ROLES.find((x) => x.id === e.target.value);
    setRoleId(r.id);
    setUsCost(r.usCost);
  };

  const usTotal = usCost * seats;
  const saveLo = usTotal * SAVE_LO;
  const saveHi = usTotal * SAVE_HI;

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-brand-ice py-28 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal><Eyebrow n="06" label="Cost calculator" /></Reveal>
        <MaskedLines
          as="h2"
          inView
          delay={0.1}
          className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl lg:text-5xl"
          lines={["What could a remote team", "save you each year?"]}
        />

        <div className="mt-16 grid gap-6 lg:grid-cols-12">
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-brand-indigo/10 bg-white p-6 sm:p-10">
              <label className="block">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-indigo/60">Role you'd hire</span>
                <select data-testid="calc-role-select" value={roleId} onChange={pickRole} className="w-full rounded-xl border border-brand-indigo/15 bg-brand-ice px-4 py-3.5 font-body text-sm text-brand-ink outline-none focus:border-brand-cyan">
                  {CALC_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </label>

              <div className="mt-8">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-brand-indigo/60">
                  <span>Number of seats</span>
                  <span data-testid="calc-seats-value" className="text-brand-ink text-sm tabular-nums">{seats}</span>
                </div>
                <input data-testid="calc-seats-slider" type="range" min={1} max={15} value={seats} onChange={(e) => setSeats(Number(e.target.value))} className="mt-3 w-full accent-brand-indigo" />
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-brand-indigo/60">
                  <span>Your all-in US cost per seat / year</span>
                  <span data-testid="calc-uscost-value" className="text-brand-ink text-sm tabular-nums">{usd(usCost)}</span>
                </div>
                <input data-testid="calc-uscost-slider" type="range" min={30000} max={120000} step={1000} value={usCost} onChange={(e) => setUsCost(Number(e.target.value))} className="mt-3 w-full accent-brand-indigo" />
                <p className="mt-2 text-xs text-slate-500">Salary, benefits, payroll tax, office and tooling. Adjust to your numbers.</p>
              </div>

              <div className="mt-8 rounded-xl bg-brand-ice px-5 py-4 text-sm text-slate-600">
                Typical time to staff this role: <span className="font-semibold text-brand-ink">{role.hireWeeks}</span>, trained on your workflows before going live.
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="flex h-full flex-col justify-between rounded-[2rem] bg-brand-navy p-6 text-white sm:p-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Estimated annual impact</div>
                <div className="mt-8 border-t border-white/10 pt-6">
                  <div className="text-sm text-white/55">Your current US cost</div>
                  <div data-testid="calc-us-total" className="mt-1 font-display text-3xl font-bold tabular-nums">{usd(usTotal)}</div>
                </div>
                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="text-sm text-white/55">With a Malansh remote team</div>
                  <div data-testid="calc-malansh-range" className="mt-1 font-display text-3xl font-bold tabular-nums">{usd(usTotal - saveHi)} – {usd(usTotal - saveLo)}</div>
                </div>
                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="text-sm text-brand-cyan">Estimated yearly savings</div>
                  <div data-testid="calc-savings-range" className="mt-1 font-display text-4xl font-extrabold tabular-nums text-brand-cyan lg:text-5xl">{usd(saveLo)} – {usd(saveHi)}</div>
                  <div className="mt-2 text-xs text-white/40">Based on a 40–60% all-in saving typical for managed remote roles. An estimate, not a quote.</div>
                </div>
              </div>
              <button data-testid="calc-cta-contact" onClick={() => scrollToId(lenis, "contact")} className="btn-primary mt-10 self-start">
                <span>Get your real numbers</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
